import type { Theme } from '@mui/material/styles';
import { TURN_STYLES } from '@/styles/theme';
import {
  AMBER,
  AMBER_SOFT,
  BLUE,
  BLUE_SOFT,
  GRAY_SOFT,
  GREEN,
  GREEN_SOFT,
  MUTED,
  NEUTRAL_TEXT,
  NEXT_STEP,
  DONE_MARKER,
  SKIPPED_MARKER,
  RED,
  RED_SOFT,
  SURFACE_SOFT,
  STEP_LABEL,
  VIOLET,
  VIOLET_DARK,
  VIOLET_SOFT,
} from './constants';
import type { IdvBannerTone } from './types';

/** Shared light/dark tokens for the ID Verification tab body. */
export const getIdvThemeTokens = (theme: Theme) => {
  const dark = theme.palette.mode === 'dark';

  const banner: Record<IdvBannerTone, { border: string; color: string; background: string }> = {
    warning: {
      border: dark ? TURN_STYLES.darkWarningDark : '#fedf89',
      color: dark ? TURN_STYLES.darkWarningLight : AMBER,
      background: dark ? 'rgba(253, 230, 138, 0.12)' : AMBER_SOFT,
    },
    success: {
      border: dark ? TURN_STYLES.darkSuccessDark : '#abefc6',
      color: dark ? TURN_STYLES.darkSuccessLight : GREEN,
      background: dark ? 'rgba(187, 247, 208, 0.12)' : GREEN_SOFT,
    },
    danger: {
      border: dark ? TURN_STYLES.darkErrorDark : '#fecdca',
      color: dark ? TURN_STYLES.darkErrorLight : RED,
      background: dark ? 'rgba(254, 202, 202, 0.12)' : RED_SOFT,
    },
    info: {
      border: dark ? TURN_STYLES.darkInfoDark : '#b2ddff',
      color: dark ? TURN_STYLES.darkInfoLight : BLUE,
      background: dark ? 'rgba(191, 219, 254, 0.12)' : BLUE_SOFT,
    },
    neutral: {
      border: dark ? TURN_STYLES.darkBorderColor : TURN_STYLES.borderColor,
      color: dark ? '#9ca3af' : NEUTRAL_TEXT,
      background: dark ? '#1c1d2e' : GRAY_SOFT,
    },
  };

  return {
    dark,
    text: dark ? TURN_STYLES.darkSecondaryColor : TURN_STYLES.neutralDark,
    textMuted: dark ? '#9ca3af' : MUTED,
    textMeta: dark ? '#9ca3af' : NEUTRAL_TEXT,
    border: dark ? TURN_STYLES.darkBorderColor : '#e4e7ec',
    borderStrong: dark ? '#5a5d82' : '#d0d5dd',
    surface: dark ? TURN_STYLES.darkSurfaceColor : TURN_STYLES.surfaceColor,
    surfaceSoft: dark ? '#1c1d2e' : SURFACE_SOFT,
    stepLabel: dark ? TURN_STYLES.darkSecondaryColor : STEP_LABEL,
    nextStep: NEXT_STEP,
    doneMarker: dark ? TURN_STYLES.darkSuccessLight : DONE_MARKER,
    skippedMarker: dark ? '#6b7280' : SKIPPED_MARKER,
    nextFill: dark ? 'rgba(253, 230, 138, 0.12)' : AMBER_SOFT,
    nextBorder: dark ? TURN_STYLES.darkWarningDark : '#fedf89',
    tagDefaultBg: dark ? '#2a2c45' : '#eaecf0',
    tagOverrideBg: dark ? 'rgba(191, 219, 254, 0.16)' : '#d1e9ff',
    tagOverrideColor: dark ? TURN_STYLES.darkInfoLight : BLUE,
    violet: dark ? TURN_STYLES.darkVioletDark : VIOLET,
    violetDark: dark ? '#7c6ef0' : VIOLET_DARK,
    violetSoft: dark ? 'rgba(91, 79, 214, 0.28)' : VIOLET_SOFT,
    green: dark ? TURN_STYLES.darkSuccessLight : GREEN,
    amber: dark ? TURN_STYLES.darkWarningLight : AMBER,
    red: dark ? TURN_STYLES.darkErrorLight : RED,
    resolvingBorder: dark ? TURN_STYLES.darkSuccessDark : '#abefc6',
    resolvingFill: dark ? 'rgba(187, 247, 208, 0.08)' : '#fbfffc',
    resolvingBar: dark ? TURN_STYLES.darkSuccessLight : DONE_MARKER,
    aiText: dark ? '#c4b5fd' : '#5b21b6',
    aiBadgeBorder: dark ? '#7c6ef0' : '#c4b5fd',
    dispWarningBg: dark ? 'rgba(253, 230, 138, 0.18)' : '#fef0c7',
    dispSolvedBg: dark ? 'rgba(187, 247, 208, 0.18)' : '#d1fadf',
    markPrimaryText: '#fff',
    markSecondaryText: dark ? TURN_STYLES.darkSecondaryColor : STEP_LABEL,
    chipHoverBorder: dark ? '#7c6ef0' : '#b9b2f1',
    colorScheme: dark ? 'dark' : 'light',
    scrim: dark ? 'rgba(0, 0, 0, 0.55)' : 'rgba(16, 24, 40, 0.08)',
    overlayShadow: dark ? '0 16px 40px rgba(0, 0, 0, 0.45)' : '0 16px 40px rgba(16, 24, 40, 0.18)',
    errorBg: dark ? 'rgba(254, 202, 202, 0.12)' : TURN_STYLES.errorLight,
    errorBorder: dark ? TURN_STYLES.darkErrorDark : '#fecaca',
    errorText: dark ? TURN_STYLES.darkErrorLight : TURN_STYLES.errorDark,
    banner,
  };
};
